<script>
import logo from "@/mixins/logo";
import userService from "@/services/user";
import { SELF_SIGNUP_ENABLED } from "@/services/constants";

/**
 * @component SelfSignUpCampana
 * @description UC-REG-254 — Portal de autoregistro del tenant Campana.
 *              Instancia PROPIA (no comparte código con `SelfSignUp.vue`,
 *              el form de Corporación Médica): decisión explícita de Diego
 *              (2026-10-08) para que las evoluciones del autoregistro de
 *              Corporación Médica no arrastren cambios a Campana, y
 *              viceversa. Se monta en `/signup` solo cuando el build es
 *              `THEME=campana` (ver `router/public.js`).
 *
 *              Backend: `POST /users/self-signup` con el motor `open`
 *              (sin validación contra padrón por ahora — la API de
 *              validación del municipio todavía no está definida). El
 *              backend crea el User paciente y manda el mail/WhatsApp con
 *              el link para definir la contraseña. No autologuea: mensaje
 *              de éxito y redirect a `/login`.
 *
 *              Campos: DNI, nombre, apellido, fecha de nacimiento, email y
 *              teléfono opcional. Se piden fecha de nacimiento y DNI
 *              aunque hoy no se validen, porque son los datos con los que
 *              la futura API va a verificar identidad.
 * @example <SelfSignUpCampana />
 */
export default {
  name: "SelfSignUpCampana",

  mixins: [logo],

  data() {
    return {
      dni: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      birthDate: null,
      isLoading: false,
      isEnabled: SELF_SIGNUP_ENABLED,
    };
  },

  created() {
    if (!this.isEnabled) {
      this.$router.replace({ name: "login" });
    }
  },

  methods: {
    /**
     * @description Payload que viaja al backend. Separado de `signUp` para
     *              poder testearlo sin red.
     * @returns {{dni:string,bornAt:string|null,email:string,firstName:string,lastName:string,phone:string}}
     */
    buildPayload() {
      return {
        dni: String(this.dni || "").trim(),
        bornAt: this.birthDate,
        email: String(this.email || "")
          .trim()
          .toLowerCase(),
        firstName: String(this.firstName || "").trim(),
        lastName: String(this.lastName || "").trim(),
        phone: String(this.phone || "").trim(),
      };
    },

    /**
     * @description Envía el registro. Éxito → aviso de que llega el mail y
     *              redirect a login. Error → lo muestra el interceptor de
     *              `services/api.js` (toast), acá solo se apaga el loading.
     */
    signUp() {
      this.isLoading = true;
      const payload = this.buildPayload();

      return userService
        .selfSignup(payload)
        .then(() => {
          this.$message.success(
            `¡Listo! Te enviamos un mail a ${payload.email} para activar tu cuenta y definir tu contraseña.`
          );
          this.$router.push({ name: "login" });
        })
        .catch(() => {})
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template lang="pug">
  .login(v-if="isEnabled")
    .login__container
      ValidationObserver(v-slot="{handleSubmit}")
        form.login__card(@submit.prevent="handleSubmit(signUp)")
          .login__img
            img.login__svg(v-if="logoLoginUrl || logoAltUrl" :src="logoLoginUrl || logoAltUrl" alt="Logo")
            micon.login__svg(v-else :name="'quantux-logo-horizontal-nuevo'")
          h2.signup__title Registrate
          p.signup__hint Completá tus datos. Te vamos a enviar un mail para activar tu cuenta y definir tu contraseña.
          ValidationProvider(name="DNI" rules="required|numeric|min:7|max:8" v-slot="{ errors }")
            el-input#dni(placeholder="DNI (sin puntos)" v-model="dni" autocapitalize="none" inputmode="numeric")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Nombre" rules="required" v-slot="{ errors }")
            el-input#firstName(placeholder="Nombre" v-model="firstName")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Apellido" rules="required" v-slot="{ errors }")
            el-input#lastName(placeholder="Apellido" v-model="lastName")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Fecha de nacimiento" rules="required" v-slot="{ errors }")
            el-date-picker.login__date(
              v-model="birthDate"
              type="date"
              placeholder="Fecha de nacimiento"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
            )
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Email" rules="required|email" v-slot="{ errors }")
            el-input#email(placeholder="Email" v-model="email" autocapitalize="none" inputmode="email")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Teléfono" v-slot="{ errors }")
            el-input#phone(placeholder="Teléfono (opcional)" v-model="phone" inputmode="tel")
            span.has-error {{ errors[0] }}
          el-button(type="primary" native-type="submit" :loading="isLoading") Registrarme
          router-link.login__link(:to="{ name: 'login' }") ¿Ya tenés cuenta? Ingresá haciendo click aquí
</template>

<style lang="scss" scoped>
.login__date {
  width: 100%;
  margin-bottom: 10px;
}
.signup__title {
  margin: 0 0 4px;
  font-size: 18px;
  color: $primary;
  text-align: center;
}
.signup__hint {
  margin: 0 0 16px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  line-height: 1.4;
}
</style>
